import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * Reactive-json primitive — renders an <a> tag whose href is built by
 * interpolating `:placeholder` tokens in a `template` string with values
 * resolved from the reactive store.
 *
 * Reactive-json's attribute interpolation can't concat segments inside an
 * HTML attribute (e.g. `href="/test/{slug}"` from a `~.slug` ref), so any
 * "build URL per row with a fixed prefix" use-case lands here. The
 * URL prefix and the placeholder names are entirely block-defined — no
 * hardcoded `/test/` or `/article/` in the front bundle.
 *
 * YAML usage :
 *   - type: PathLink
 *     template: "/test/:slug"             # block decides its own pattern
 *     values:
 *       slug: ~.slug                       # placeholder → ref
 *     attributes:                          # any HTML attrs (class, target, …)
 *       class: "btn-primary"
 *       target: _blank
 *     content: "Voir"
 *
 * Multi-placeholder example :
 *   - type: PathLink
 *     template: "/article/:year/:slug"
 *     values:
 *       year: ~.objectData.data.year
 *       slug: ~.slug
 *
 * Notes :
 *   • Placeholder values are URL-encoded.
 *   • An empty / undefined value for a placeholder leaves the token in the
 *     output (`/test/:slug`) — easier to spot the bug than silently
 *     producing `/test/`.
 *   • If `template` is missing, falls back to `href="#"` so the link is
 *     a noop instead of crashing.
 */
export const PathLink = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const template = evaluateTemplateValue({
    valueToEvaluate: props.template,
    globalDataContext,
    templateContext,
  })

  const values = evaluateTemplateValueCollection({
    valueToEvaluate: props.values ?? {},
    globalDataContext,
    templateContext,
  })

  let href = typeof template === 'string' && template.length > 0 ? template : '#'
  if (values && typeof values === 'object') {
    for (const [key, val] of Object.entries(values)) {
      if (val == null || val === '') continue
      href = href.replace(
        new RegExp(`:${key}(?![A-Za-z0-9_])`, 'g'),
        encodeURIComponent(String(val)),
      )
    }
  }

  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)
  const { target } = evaluatedAttrs
  const rel = target === '_blank' ? (evaluatedAttrs.rel ?? 'noopener noreferrer') : evaluatedAttrs.rel

  return (
    <ActionDependant {...props}>
      <a {...evaluatedAttrs} href={href} rel={rel}>
        {props.content && (
          <View
            currentData={currentData?.content}
            datafield="content"
            path={path + '.content'}
            props={props.content}
          />
        )}
      </a>
    </ActionDependant>
  )
}
